import { useMemo, useRef, useState } from 'react'
import { splitFormulaTokens } from '@/lib/formula'
import {
  backspaceTokens,
  clampCaret,
  insertTokensAt,
  removeTokenAt,
  replaceTokenAt,
  type Caret,
} from '@/lib/formulaTokens'

const HISTORY_LIMIT = 50

/**
 * Shared caret + in-place editing state behind the formula chip builders
 * (`FormulaBuilder`, `CellFormulaBuilder`, `LibraryFormulaBuilder`). Wraps the
 * pure helpers in `formulaTokens.ts` in React state and keeps the previous
 * append-only surface (`insert`/`backspace`/`clear`) working unchanged for
 * callers that don't care about the caret.
 *
 * Undo/redo history is local to this hook instance (i.e. the panel) and is
 * lost on unmount — there is no cross-session persistence.
 */
export function useFormulaChipEditor(formula: string, onChange: (formula: string) => void) {
  const tokens = useMemo(() => splitFormulaTokens(formula), [formula])
  const [caret, setCaretRaw] = useState<Caret>(null)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

  // Undo/redo stacks hold prior *formula strings* (not token arrays) so they
  // stay valid across the token-array identity changes each commit causes.
  const historyRef = useRef<string[]>([])
  const futureRef = useRef<string[]>([])

  const setCaret = (next: Caret) => {
    setCaretRaw(next === null ? null : clampCaret(next, tokens.length))
    setSelectedIndex(null)
  }

  const commit = (nextTokens: string[], nextCaret: Caret, recordHistory = true) => {
    if (recordHistory) {
      historyRef.current = [...historyRef.current, formula].slice(-HISTORY_LIMIT)
      futureRef.current = []
    }
    setSelectedIndex(null)
    setCaretRaw(nextCaret)
    onChange(nextTokens.join(' '))
  }

  /** Insert one or more tokens (a whole snippet, e.g. a structured IF) at the
   *  caret — or append when no caret is placed (unchanged legacy behavior). */
  const insert = (newTokens: string[]) => {
    const result = insertTokensAt(tokens, caret, newTokens)
    commit(result.tokens, result.caret)
  }

  /** Swap the token at `index` in place; every other token is untouched. */
  const replace = (index: number, newToken: string) => {
    commit(replaceTokenAt(tokens, index, newToken), index + 1)
  }

  /** Remove exactly the token at `index`. */
  const removeAt = (index: number) => {
    const result = removeTokenAt(tokens, index)
    commit(result.tokens, result.caret)
  }

  /** Removes the last token regardless of caret position — the existing
   *  backspace button's behavior, unchanged. */
  const backspace = () => {
    const result = backspaceTokens(tokens, null)
    commit(result.tokens, result.caret)
  }

  const clear = () => commit([], null)

  const canUndo = historyRef.current.length > 0
  const canRedo = futureRef.current.length > 0

  const undo = () => {
    const prev = historyRef.current[historyRef.current.length - 1]
    if (prev === undefined) return
    historyRef.current = historyRef.current.slice(0, -1)
    futureRef.current = [...futureRef.current, formula]
    commit(splitFormulaTokens(prev), null, false)
  }

  const redo = () => {
    const next = futureRef.current[futureRef.current.length - 1]
    if (next === undefined) return
    futureRef.current = futureRef.current.slice(0, -1)
    historyRef.current = [...historyRef.current, formula]
    commit(splitFormulaTokens(next), null, false)
  }

  return {
    tokens,
    caret,
    setCaret,
    selectedIndex,
    setSelectedIndex,
    insert,
    replace,
    removeAt,
    backspace,
    clear,
    undo,
    redo,
    canUndo,
    canRedo,
  }
}

export type FormulaChipEditor = ReturnType<typeof useFormulaChipEditor>
